import { useState } from "react";
import { nameParts, searchByName, type OwnerLinked, type OwnerWaiting } from "@teamna/shared";
import { useUnlinkNewParticipant } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, SearchField, SegmentedControl, showToast } from "../components/index.ts";
import { phoneText } from "./families/model.ts";

/**
 * The Swimmers panel of `/owner/new-participants` (link-parents-sync): every name of this round,
 * by level, with a search and a filter. A name to find opens its card in "Still missing"; a linked
 * name shows who it went to and how, with Unlink.
 */

type Filter = "all" | "find" | "check" | "linked";

const HOW: Record<string, string> = {
  match: "A match from Teamna, you said yes",
  owner: "You linked it",
};

export function SwimmersPanel({
  businessId,
  waiting,
  linked,
  checkIds = new Set(),
  onOpenWaiting,
}: {
  businessId: string;
  waiting: OwnerWaiting[];
  linked: OwnerLinked[];
  /** Waiting names with an answer from a parent or a coach to check. */
  checkIds?: ReadonlySet<string>;
  onOpenWaiting: (id: string) => void;
}) {
  const { label } = useLabels();
  const unlink = useUnlinkNewParticipant(businessId);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<string | null>(null);
  type Row = { id: string; name: string; groupName: string; coach: string; coaches: string[]; linked?: OwnerLinked };
  const shown: Row[] = [
    ...(filter === "linked" ? [] : waiting.filter((w) => (filter !== "check" || checkIds.has(w.id)) && (filter !== "find" || !checkIds.has(w.id))).map((w) => ({ id: w.id, name: w.name, groupName: w.groupName, coach: w.coachNames[0]?.split(" ")[0] ?? "", coaches: w.coachNames }))),
    ...(filter === "find" || filter === "check"
      ? []
      : linked.map((l) => ({ id: l.id, name: l.name, groupName: l.groupName, coach: l.coachNames[0]?.split(" ")[0] ?? "", coaches: l.coachNames, linked: l }))),
  ];
  // The name search rule: a swimmer by name (other spellings too), or the swimmers of a coach.
  const found = searchByName(shown, q, (row) => [row.name, { text: nameParts(row.name, ...row.coaches), via: true }]);
  const rows = found.rows;
  useTrackNameSearch("new_participants", q, found.results);
  const levels = [...new Set(rows.map((r) => r.groupName))].sort();
  // "3/11" next to each level: names linked out of all the names of the level on this page.
  const levelCount = (level: string) => {
    const done = linked.filter((l) => l.groupName === level).length;
    return `${done}/${done + waiting.filter((w) => w.groupName === level).length}`;
  };
  // The other names linked to the same family by this page: "Unlink all 3 from Hala".
  const sameFamily = (row: OwnerLinked) => linked.filter((l) => l.id !== row.id && l.familyId && l.familyId === row.familyId && l.linkKind);
  const [busyAll, setBusyAll] = useState(false);

  const doUnlink = (row: OwnerLinked) =>
    unlink.mutate(row.id, {
      onSuccess: () => {
        setOpen(null);
        showToast(`${row.name} unlinked. ${row.name.split(" ")[0]} waits for a ${label("member")} again.`);
      },
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });

  const doUnlinkAll = async (rowsToUnlink: OwnerLinked[], parent: string) => {
    setBusyAll(true);
    let done = 0;
    let problem: unknown = null;
    for (const row of rowsToUnlink) {
      try {
        // oxlint-disable-next-line no-await-in-loop -- one at a time, like the links
        await unlink.mutateAsync(row.id);
        done += 1;
      } catch (error) {
        problem = error;
      }
    }
    setBusyAll(false);
    setOpen(null);
    showToast(
      problem
        ? `${done} unlinked. Some could not be unlinked: ${messageForError(problem)}`
        : `${done} ${label("participant", { plural: true })} unlinked from ${parent}. They wait for a ${label("member")} again.`,
      problem ? { tone: "info" } : undefined,
    );
  };

  return (
    <aside className="flex flex-col gap-3 rounded-card border border-line bg-paper p-4" aria-label={label("participant", { plural: true, capital: true })}>
      <h2 className="text-[16px] font-bold text-ink">{label("participant", { plural: true, capital: true })}</h2>
      <SearchField
        label={`Search ${label("participant", { plural: true })}`}
        placeholder={`Search a ${label("participant")} or a ${label("staff")}`}
        value={q}
        onChange={setQ}
      />
      <SegmentedControl<Filter>
        label="Show"
        size="compact"
        value={filter}
        onChange={setFilter}
        options={[
          { value: "all", label: `All ${waiting.length + linked.length}` },
          { value: "find", label: `To find ${waiting.filter((w) => !checkIds.has(w.id)).length}` },
          ...(checkIds.size ? [{ value: "check" as const, label: `Check ${waiting.filter((w) => checkIds.has(w.id)).length}` }] : []),
          { value: "linked", label: `Linked ${linked.length}` },
        ]}
      />
      <div className="flex max-h-[560px] flex-col overflow-y-auto">
        {rows.length === 0 ? <p className="py-2 text-[13px] text-ink-2">Nobody here.</p> : null}
        {levels.map((level) => (
          <section key={level} aria-label={level}>
            <p className="eyebrow flex justify-between pt-3 pb-1 text-ink-3">
              <span>{level}</span>
              <span className="tabular-nums">{levelCount(level)}</span>
            </p>
            {rows
              .filter((r) => r.groupName === level)
              .map((r) => {
                const l = r.linked;
                const isOpen = open === r.id;
                return (
                  <div key={r.id}>
                    <button
                      type="button"
                      aria-expanded={l ? isOpen : undefined}
                      onClick={() => (l ? setOpen(isOpen ? null : r.id) : onOpenWaiting(r.id))}
                      className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[13.5px] hover:bg-sunken"
                    >
                      <span className={`min-w-0 ${l ? "text-ink-3" : "text-ink"}`}>
                        {r.name} <span className="text-[12px] text-ink-3">· {r.coach}</span>
                      </span>
                      {l ? (
                        <span className="shrink-0 text-right text-[12px] font-semibold text-confirmed-ink">✓ {l.parentName?.split(" ")[0] ?? "Linked"}</span>
                      ) : checkIds.has(r.id) ? (
                        <span className="shrink-0 rounded-full bg-confirmed-surface px-2 py-0.5 text-[11.5px] font-bold text-whatsapp">Check</span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-late-surface px-2 py-0.5 text-[11.5px] font-bold text-late-ink">To find</span>
                      )}
                    </button>
                    {l && isOpen ? (
                      <div className="mx-2 mb-2 flex flex-col gap-1 rounded-lg bg-sunken px-3 py-2 text-[12.5px]">
                        <span className="text-ink">
                          {l.parentName ?? `A ${label("member")}`}
                          {l.parentPhoneE164 ? `, ${phoneText(l.parentPhoneE164)}` : ""}
                        </span>
                        <span className="text-ink-2">{l.linkedVia ? HOW[l.linkedVia] : "Linked"}</span>
                        {l.linkKind ? (
                          <div className="flex flex-wrap gap-2">
                            <Button variant="secondary" size="sm" loading={unlink.isPending && !busyAll} disabled={busyAll} onClick={() => doUnlink(l)}>
                              Unlink {l.name.split(" ")[0]}
                            </Button>
                            {sameFamily(l).length ? (
                              <Button variant="ghost" size="sm" loading={busyAll} onClick={() => void doUnlinkAll([l, ...sameFamily(l)], l.parentName?.split(" ")[0] ?? `the ${label("member")}`)}>
                                Unlink all {sameFamily(l).length + 1} from {l.parentName?.split(" ")[0] ?? `the ${label("member")}`}
                              </Button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-ink-3">Linked before Unlink was possible. Use the Families page.</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
          </section>
        ))}
      </div>
    </aside>
  );
}
